import React, { useState, useEffect, useMemo } from "react";
import CopyIdButton from "../components/CopyIdButton";
import { useTableColumnFilters, FilterTh, SortFilterTh } from "../components/tableFilters";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import * as XLSX from "xlsx";
import { Order, OrderStatus, Workshop, OrderUnit, Technic } from "../types";
import {
  orderService,
  workshopService,
} from "../services/dataService";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionContext";
import { useExchangeRates } from "../context/ExchangeRateContext";
import ImageManager from "../components/ImageManager";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import OrderCostsModal from "../components/OrderCostsModal";
import NoteModal from "../components/NoteModal";
import {
  formatCurrency,
  formatNumber,
  turkishIncludes,
} from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import OrderDetailModal from "../components/OrderDetailModal";
import OrderEditModal from "../components/OrderEditModal";
import { FirmMultiPickerField } from "../components/FirmPicker";
import TechnicModal from "../components/TechnicModal";
import {
  TechnicMatchMode,
  TECHNIC_MATCH_MODES,
  getTechnicMatchHint,
  matchesTechnicFilter,
} from "../utils/technicMatch";
import "./PurchaseOrders.css";
import "./Orders.css";

const STORAGE_KEY = "orders-filters";

const svg = (d: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const I = {
  search: svg(<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>),
  close: svg(<path d="M6 6l12 12M18 6 6 18" />),
  plus: svg(<path d="M12 5v14M5 12h14" />),
  download: svg(<path d="M12 3v12m0 0-4-4m4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />),
  print: svg(<><path d="M6 9V3h12v6" /><rect x="3" y="9" width="18" height="8" rx="2" /><path d="M7 14h10v7H7z" /></>),
  filter: svg(<path d="M3 5h18l-7 8v6l-4-2v-4z" />),
  filterOff: svg(<><path d="M3 5h12M19 5h2l-4.5 5.2M10 13v4l4 2v-5" /><path d="M3 3l18 18" /></>),
  caret: svg(<path d="m6 9 6 6 6-6" />),
  factory: svg(<><path d="M3 21V9l6 4V9l6 4V5h6v16z" /><path d="M7 17h2M13 17h2" /></>),
  layers: svg(<><path d="M12 2 2 7l10 5 10-5z" /><path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></>),
  inbox: svg(<><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>),
  dots: svg(<><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></>),
  eye: svg(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></>),
  edit: svg(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></>),
  transfer: svg(<path d="M17 3h4v4M21 3l-7 7M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />),
  image: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>),
  qr: svg(<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>),
  money: svg(<path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />),
  trash: svg(<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />),
  note: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>),
};

/** Durum → po-badge tonu */
const statusBadge = (status?: OrderStatus): string => {
  switch (status) {
    case OrderStatus.Islemde: return "info";
    case OrderStatus.Tamamlandi: return "ok";
    case OrderStatus.IptalEdildi: return "err";
    default: return "warn"; // Atanmadı
  }
};

/** Termini geçmiş, kapanmamış sipariş (yalnız görsel vurgu) */
const isLate = (o: Order): boolean => {
  if (!o.deadline || o.status === OrderStatus.Tamamlandi || o.status === OrderStatus.IptalEdildi) return false;
  const d = new Date(o.deadline);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

// localStorage'dan filtreleri yükle
const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        statusFilter: parsed.statusFilter || [],
        firmFilter: parsed.firmFilter || [],
        workshopFilter: parsed.workshopFilter || [],
        dateFilter: parsed.dateFilter || { startDate: "", endDate: "" },
        searchTerm: parsed.searchTerm || "",
        activeStatusFilter: parsed.activeStatusFilter || "active",
        selectedTechnics: parsed.selectedTechnics || [],
        technicMatchMode: parsed.technicMatchMode || "exact",
      };
    }
  } catch (error) {
    console.error("Failed to load filters from storage:", error);
  }
  return {
    statusFilter: [],
    firmFilter: [],
    workshopFilter: [],
    dateFilter: { startDate: "", endDate: "" },
    searchTerm: "",
    activeStatusFilter: "active",
    selectedTechnics: [],
    technicMatchMode: "exact",
  };
};

const Orders: React.FC = () => {
  const savedFilters = loadFiltersFromStorage();

  const [orders, setOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]); // Filtrelenmemiş tüm siparişler
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>(
    savedFilters.statusFilter,
  );
  const [firmFilter, setFirmFilter] = useState<string[]>(
    savedFilters.firmFilter,
  );
  const [workshopFilter, setWorkshopFilter] = useState<string[]>(
    savedFilters.workshopFilter,
  );
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>(
    savedFilters.activeStatusFilter,
  );
  const [workshopSearch, setWorkshopSearch] = useState<string>("");
  const [workshopDropdownOpen, setWorkshopDropdownOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: savedFilters.dateFilter.startDate || "",
    endDate: savedFilters.dateFilter.endDate || "",
  });
  const [summaryDateFilter, setSummaryDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  // Teknik filtresi
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>(
    savedFilters.selectedTechnics,
  );
  const [technicMatchMode, setTechnicMatchMode] = useState<TechnicMatchMode>(
    savedFilters.technicMatchMode,
  );
  const [showTechnicModal, setShowTechnicModal] = useState(false);
  // Mobil: filtre paneli varsayılan kapalı; arama + "Filtreler" düğmesi her zaman görünür
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  // QR Print Modal state
  const [qrPrintModal, setQrPrintModal] = useState<{
    show: boolean;
    order: Order | null;
    qrCodeDataUrl: string | null;
  }>({ show: false, order: null, qrCodeDataUrl: null });

  // Order Costs Modal state
  const [orderCostsModal, setOrderCostsModal] = useState<{
    isOpen: boolean;
    orderId: string;
    modelId: string;
  }>({ isOpen: false, orderId: "", modelId: "" });

  // Arama ve sıralama state'leri
  const [searchTerm, setSearchTerm] = useState<string>(savedFilters.searchTerm);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const [expandedTechnics, setExpandedTechnics] = useState<Set<string>>(
    new Set(),
  );

  // Note Modal state
  const [noteModal, setNoteModal] = useState<{
    isOpen: boolean;
    note: string;
    orderCode: string;
    modelName: string;
  }>({ isOpen: false, note: "", orderCode: "", modelName: "" });
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });

  const navigate = useNavigate();
  const { user } = useAuth();
  const { has } = usePermissions();
  const { usdRate, eurRate } = useExchangeRates();

  // Yetki matrisi (backend de aynı kodları zorunlu kılar)
  const canUpdateOrder = has("orders:update");
  const canDeleteOrder = has("orders:delete");
  const canViewModelCosts = has("model-costs:view");

  // Numune durumunu toggle et
  const handleToggleSample = async (order: Order) => {
    try {
      const newSampleStatus = !order.isSample;

      await orderService.updateSampleStatus(order.orderId, newSampleStatus);

      // Local state'i güncelle
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === order.orderId ? { ...o, isSample: newSampleStatus } : o,
        ),
      );

      setAllOrders((prev) =>
        prev.map((o) =>
          o.orderId === order.orderId ? { ...o, isSample: newSampleStatus } : o,
        ),
      );
    } catch (error) {
      console.error("Numune durumu güncellenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Numune durumu güncellenemedi!",
        type: "error",
      });
    }
  };

  // Sipariş silme fonksiyonu
  const handleDeleteOrder = async (order: Order) => {
    setConfirmModal({
      isOpen: true,
      message: `"${order.firm?.firmName || "Firma"} - ${
        order.model?.modelName || "Model"
      }" siparişini silmek istediğinizden emin misiniz?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          await orderService.delete(order.orderId);

          // Local state'leri güncelle
          setOrders((prev) => prev.filter((o) => o.orderId !== order.orderId));
          setAllOrders((prev) =>
            prev.filter((o) => o.orderId !== order.orderId),
          );

          setAlertModal({
            isOpen: true,
            message: "Sipariş başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("Sipariş silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Sipariş silinemedi!",
            type: "error",
          });
        }
      },
    });
  };

  // QR Modal'ı aç
  const handleShowQrModal = async (order: Order) => {
    let qrDataUrl: string | null = null;
    try {
      if (order.qrCodeUrl || order.qrCode) {
        qrDataUrl = await QRCode.toDataURL(
          order.qrCodeUrl || order.qrCode || "",
          {
            width: 300,
            margin: 2,
          },
        );
      }
    } catch (error) {
      console.error("QR kod oluşturulamadı:", error);
    }

    setQrPrintModal({ show: true, order, qrCodeDataUrl: qrDataUrl });
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    firmFilter,
    workshopFilter,
    dateFilter,
    activeStatusFilter,
  ]);

  // Filtreleri localStorage'a kaydet
  useEffect(() => {
    const filters = {
      statusFilter,
      firmFilter,
      workshopFilter,
      dateFilter,
      searchTerm,
      activeStatusFilter,
      selectedTechnics,
      technicMatchMode,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    statusFilter,
    firmFilter,
    workshopFilter,
    dateFilter,
    searchTerm,
    activeStatusFilter,
    selectedTechnics,
    technicMatchMode,
  ]);

  useEffect(() => {
    loadWorkshops();
  }, []);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".ol-ws")) setWorkshopDropdownOpen(false);
      if (!target.closest(".ol-menubtn") && !target.closest(".ol-menu")) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Esc: önce QR penceresi, sonra satır menüsü / atölye listesi kapanır
  useEffect(() => {
    if (!openDropdownId && !qrPrintModal.show && !workshopDropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (qrPrintModal.show) setQrPrintModal({ show: false, order: null, qrCodeDataUrl: null });
      else {
        setOpenDropdownId(null);
        setDropdownPosition(null);
        setWorkshopDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [openDropdownId, qrPrintModal.show, workshopDropdownOpen]);

  // Scroll olduğunda dropdown'u kapat
  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openDropdownId]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      // Aktif/Pasif filtresine göre doğru endpoint'i çağır
      let data: Order[];
      if (activeStatusFilter === "active") {
        data = await orderService.getAll(); // Sadece aktif siparişler
      } else if (activeStatusFilter === "inactive") {
        data = await orderService.getPassiveOrders(); // Sadece pasif siparişler
      } else {
        data = await orderService.getAllOrders(); // Tüm siparişler
      }

      // API'dan zaten createdAt'a göre sıralı geliyor
      setAllOrders(data); // Tüm siparişleri kaydet

      let filteredData = data;

      // Manager olmayan kullanıcılar sadece kendi atölyelerindeki işleri görebilir
      if (
        user &&
        user.role !== "Manager" &&
        user.workshops &&
        user.workshops.length > 0
      ) {
        const userWorkshopIds = user.workshops.map((w) => w.workshopId);
        filteredData = filteredData.filter(
          (order) =>
            order.workshopId && userWorkshopIds.includes(order.workshopId),
        );
      }

      if (statusFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) =>
            order.status !== undefined && statusFilter.includes(order.status),
        );
      }
      if (firmFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) => order.firmId && firmFilter.includes(order.firmId),
        );
      }
      if (workshopFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) =>
            order.workshopId && workshopFilter.includes(order.workshopId),
        );
      }

      // Tarih filtresi - sadece "Tamamlandı" status'unda çalışır
      if (
        statusFilter.length === 1 &&
        statusFilter.includes(OrderStatus.Tamamlandi) &&
        (dateFilter.startDate || dateFilter.endDate)
      ) {
        filteredData = filteredData.filter((order) => {
          if (!order.completionDate) return false;

          const orderDate = new Date(order.completionDate);
          const startDate = dateFilter.startDate
            ? new Date(dateFilter.startDate)
            : null;
          const endDate = dateFilter.endDate
            ? new Date(dateFilter.endDate)
            : null;

          if (startDate && orderDate < startDate) return false;
          if (endDate && orderDate > endDate) return false;

          return true;
        });
      }

      setOrders(filteredData);
    } catch (error: any) {
      console.error("❌ Failed to load orders:", error);
      setAlertModal({
        isOpen: true,
        message: "Siparişler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  const calculateStatusSummary = (ordersList: Order[]) => {
    let filteredOrders = ordersList;

    // Durum özeti için tarih filtresi uygula
    if (summaryDateFilter.startDate || summaryDateFilter.endDate) {
      filteredOrders = ordersList.filter((order) => {
        if (!order.completionDate) return false;

        const orderDate = new Date(order.completionDate);
        const startDate = summaryDateFilter.startDate
          ? new Date(summaryDateFilter.startDate)
          : null;
        const endDate = summaryDateFilter.endDate
          ? new Date(summaryDateFilter.endDate)
          : null;

        if (startDate && orderDate < startDate) return false;
        if (endDate && orderDate > endDate) return false;

        return true;
      });
    }

    const summary: {
      [key: string]: {
        Count: number;
        TotalValue: number;
        TotalQuantity: number;
      };
    } = {};

    filteredOrders.forEach((order) => {
      const statusKey = getStatusText(order.status);
      if (!summary[statusKey]) {
        summary[statusKey] = { Count: 0, TotalValue: 0, TotalQuantity: 0 };
      }
      summary[statusKey].Count += 1;
      summary[statusKey].TotalQuantity += order.quantity || 0;

      // Tüm siparişler için fiyat hesapla (fiyat varsa)
      if (order.price && order.quantity) {
        const totalPrice = order.price * order.quantity;

        // Döviz cinsine göre TL'ye çevir
        let priceInTRY = totalPrice;
        const currency = order.priceCurrency || order.currency || "TRY";

        if (currency === "USD" && usdRate) {
          priceInTRY = totalPrice * usdRate;
        } else if (currency === "EUR" && eurRate) {
          priceInTRY = totalPrice * eurRate;
        }

        summary[statusKey].TotalValue += priceInTRY;
      }
    });

    return Object.entries(summary).map(([Status, data]) => ({
      Status,
      Count: data.Count,
      TotalValue: data.TotalValue,
      TotalQuantity: data.TotalQuantity,
    }));
  };

  const loadWorkshops = async () => {
    try {
      const data = await workshopService.getAll();
      setWorkshops(data);
    } catch (error) {
      console.error("❌ Failed to load workshops:", error);
    }
  };

  // Durum kartına tıklandığında filtreleme yap
  const handleStatusCardClick = (statusText: string) => {
    // Durum metnine göre OrderStatus değerini bul
    let newStatus: OrderStatus | null = null;

    switch (statusText) {
      case "Atanmadı":
        newStatus = OrderStatus.Atanmadi;
        break;
      case "İşlemde":
        newStatus = OrderStatus.Islemde;
        break;
      case "Tamamlandı":
        newStatus = OrderStatus.Tamamlandi;
        break;
      case "İptal Edildi":
        newStatus = OrderStatus.IptalEdildi;
        break;
    }

    if (newStatus !== null) {
      setStatusFilter([newStatus]);

      // Tamamlandı dışındaki durumlar için tarih filtrelerini sıfırla
      if (newStatus !== OrderStatus.Tamamlandi) {
        setDateFilter({ startDate: "", endDate: "" });
      }
    }
  };

  const getStatusText = (status?: OrderStatus): string => {
    if (status === undefined || status === null) return "Atanmadı";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "Atanmadı";
      case OrderStatus.Islemde:
        return "İşlemde";
      case OrderStatus.Tamamlandi:
        return "Tamamlandı";
      case OrderStatus.IptalEdildi:
        return "İptal Edildi";
      default:
        return "Atanmadı";
    }
  };

  const getUnitText = (unit?: OrderUnit): string => {
    const unitMap: { [key: number]: string } = {
      [OrderUnit.Adet]: "Adet",
      [OrderUnit.Metre]: "Metre",
      [OrderUnit.Takim]: "Takım",
      [OrderUnit.Pano]: "Pano",
    };
    return unitMap[unit ?? OrderUnit.Adet] || "Adet";
  };

  // Sıralama fonksiyonu
  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Verileri sıralayan fonksiyon
  const getSortedOrders = (ordersToSort: Order[]) => {
    // Eğer manuel sıralama yoksa, API'dan gelen sırayı koru
    if (!sortConfig.key) {
      return ordersToSort;
    }

    const sortedOrders = [...ordersToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case "firm":
          aValue = a.firm?.firmName || "";
          bValue = b.firm?.firmName || "";
          break;
        case "model":
          aValue = a.model?.modelName || "";
          bValue = b.model?.modelName || "";
          break;
        case "quantity":
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case "price":
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case "workshop":
          aValue = a.workshop?.name || "";
          bValue = b.workshop?.name || "";
          break;
        case "operator":
          aValue = a.operator
            ? `${a.operator.firstName} ${a.operator.lastName}`
            : "";
          bValue = b.operator
            ? `${b.operator.firstName} ${b.operator.lastName}`
            : "";
          break;
        case "modelist":
          aValue = a.modelistUser
            ? `${a.modelistUser.firstName} ${a.modelistUser.lastName}`
            : "";
          bValue = b.modelistUser
            ? `${b.modelistUser.firstName} ${b.modelistUser.lastName}`
            : "";
          break;
        case "status":
          aValue = a.status || 0;
          bValue = b.status || 0;
          break;
        case "acceptanceDate":
          aValue = a.acceptanceDate ? new Date(a.acceptanceDate).getTime() : 0;
          bValue = b.acceptanceDate ? new Date(b.acceptanceDate).getTime() : 0;
          break;
        case "deadline":
          aValue = a.deadline ? new Date(a.deadline).getTime() : 0;
          bValue = b.deadline ? new Date(b.deadline).getTime() : 0;
          break;
        case "completionDate":
          aValue = a.completionDate ? new Date(a.completionDate).getTime() : 0;
          bValue = b.completionDate ? new Date(b.completionDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sortedOrders;
  };

  // Teknik seçimi (aç/kapa)
  const handleTechnicSelect = (technic: Technic) => {
    setSelectedTechnics((prev) =>
      prev.some((t) => t.technicId === technic.technicId)
        ? prev.filter((t) => t.technicId !== technic.technicId)
        : [...prev, technic],
    );
  };

  const handleClearTechnics = () => setSelectedTechnics([]);

  // Teknik filtresi - eşleşme moduna göre
  const getTechnicFilteredOrders = (ordersToFilter: Order[]) => {
    if (selectedTechnics.length === 0) return ordersToFilter;

    const selectedTechnicNames = selectedTechnics.map((t) => t.name);
    return ordersToFilter.filter((order) => {
      const orderTechnicNames = (order.orderTechnics || [])
        .map((ot) => ot.technic?.name || "")
        .filter(Boolean);
      return matchesTechnicFilter(
        orderTechnicNames,
        selectedTechnicNames,
        technicMatchMode,
      );
    });
  };

  // Arama fonksiyonu
  const getFilteredOrders = (ordersToFilter: Order[]) => {
    if (!searchTerm.trim()) return ordersToFilter;

    return ordersToFilter.filter((order) => {
      return (
        turkishIncludes(order.orderId || "", searchTerm) ||
        turkishIncludes(order.firm?.firmName || "", searchTerm) ||
        turkishIncludes(order.model?.modelName || "", searchTerm) ||
        turkishIncludes(order.model?.modelCode || "", searchTerm) ||
        turkishIncludes(order.workshop?.name || "", searchTerm) ||
        turkishIncludes(order.operator?.firstName || "", searchTerm) ||
        turkishIncludes(order.operator?.lastName || "", searchTerm) ||
        turkishIncludes(order.modelistUser?.firstName || "", searchTerm) ||
        turkishIncludes(order.modelistUser?.lastName || "", searchTerm) ||
        turkishIncludes(getStatusText(order.status), searchTerm) ||
        order.quantity?.toString().includes(searchTerm.trim()) ||
        order.price?.toString().includes(searchTerm.trim())
      );
    });
  };

  // Hem aramayı hem sıralamayı uygulayan fonksiyon
  const getProcessedOrders = () => {
    const filtered = getFilteredOrders(getTechnicFilteredOrders(orders));
    return getSortedOrders(filtered);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Excel'e aktar fonksiyonu
  const exportToExcel = () => {
    const dataToExport = getProcessedOrders().map((order) => {
      const rowData: any = {
        "Order ID": order.orderId,
        Firma: order.firm?.firmName || "-",
        Model: order.model?.modelName || "-",
        Miktar: order.quantity || 0,
        Birim: getUnitText(order.unit),
        "Parça/Takım":
          order.unit === OrderUnit.Takim && order.pieceCount
            ? `${order.pieceCount} parça`
            : "-",
        Numune: order.isSample ? "Evet" : "Hayır",
        Teknikler:
          order.orderTechnics
            ?.map((ot) => ot.technic?.name || "-")
            .join(", ") || "-",
      };

      // Manager için fiyat bilgilerini ekle
      if (user?.role === "Manager") {
        rowData["Birim Fiyat"] = order.price ? formatNumber(order.price) : "-";
        rowData["Döviz"] = order.priceCurrency || order.currency || "TRY";
      }

      rowData["Atölye"] = order.workshop?.name || "-";
      rowData["Operatör"] = order.operator
        ? `${order.operator.firstName} ${order.operator.lastName}`
        : "-";
      rowData["Modelist"] = order.modelistUser
        ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}`
        : "-";
      rowData["Durum"] = getStatusText(order.status);
      rowData["Kabul Tarihi"] = formatDate(order.acceptanceDate);
      rowData["Termin"] = formatDate(order.deadline);
      rowData["Bitiş Tarihi"] = formatDate(order.completionDate);

      return rowData;
    });

    // Worksheet oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Siparişler");

    // Excel dosyasını indir
    const fileName = `Siparisler_${new Date()
      .toLocaleDateString("tr-TR")
      .replace(/\./g, "-")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const processedOrders = getProcessedOrders();
  const orderColDefs = useMemo(
    () => [
      { key: "orderId", value: (o: Order) => o.orderId || "" },
      { key: "firm", value: (o: Order) => o.firm?.firmName || "" },
      { key: "model", value: (o: Order) => o.model?.modelName || "" },
      { key: "quantity", type: "float", value: (o: Order) => o.quantity ?? null },
      { key: "unit", value: (o: Order) => getUnitText(o.unit) },
      { key: "pieceCount", type: "int", value: (o: Order) => o.pieceCount ?? null },
      { key: "technics", value: (o: Order) => (o.orderTechnics || []).map((ot) => ot.technic?.name || "").filter(Boolean).join(", ") },
      { key: "price", type: "decimal", value: (o: Order) => o.price ?? null },
      { key: "currency", value: (o: Order) => o.priceCurrency || o.currency || "TRY" },
      { key: "workshop", value: (o: Order) => o.workshop?.name || "" },
      { key: "modelist", value: (o: Order) => (o.modelistUser ? `${o.modelistUser.firstName} ${o.modelistUser.lastName}`.trim() : "") },
      { key: "status", value: (o: Order) => getStatusText(o.status) },
      { key: "acceptanceDate", type: "datetime", value: (o: Order) => o.acceptanceDate || null },
      { key: "deadline", type: "datetime", value: (o: Order) => o.deadline || null },
      { key: "completionDate", type: "datetime", value: (o: Order) => o.completionDate || null },
      { key: "note", value: (o: Order) => o.note || "" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: orderCf, filtered: displayOrders } = useTableColumnFilters(processedOrders, orderColDefs);
  // Durum özeti tabloyla aynı kümeden hesaplanır: üst filtreler + arama + teknik + sütun filtreleri (displayOrders)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusSummary = useMemo(() => calculateStatusSummary(displayOrders), [displayOrders, usdRate, eurRate, summaryDateFilter]);

  const activeFilterCount = [
    statusFilter.length > 0,
    firmFilter.length > 0,
    workshopFilter.length > 0,
    !!(dateFilter.startDate || dateFilter.endDate),
    selectedTechnics.length > 0,
    activeStatusFilter !== "active",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter([]);
    setFirmFilter([]);
    setWorkshopFilter([]);
    setDateFilter({ startDate: "", endDate: "" });
    setSearchTerm("");
    setActiveStatusFilter("active");
    setSelectedTechnics([]);
    setTechnicMatchMode("exact");
    localStorage.removeItem(STORAGE_KEY);
  };

  const closeQr = () => setQrPrintModal({ show: false, order: null, qrCodeDataUrl: null });

  // QR kodunu yeni pencerede yazdır
  const printQr = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow && qrPrintModal.qrCodeDataUrl && qrPrintModal.order) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Kod - ${qrPrintModal.order.orderId}</title>
            <style>
              body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: Arial, sans-serif; }
              .qr-container { text-align: center; padding: 1.25rem; }
              img { width: 21.875rem; height: 21.875rem; margin: 1.25rem 0; }
              .info { margin: 0.625rem 0; font-size: 1.875rem; }
              h2 { font-size: 1.875rem; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <h2>Sipariş QR Kodu</h2>
              <img src="${qrPrintModal.qrCodeDataUrl}" alt="QR Code" />
              <div class="info"><strong>Sipariş No:</strong> ${qrPrintModal.order.orderId}</div>
              <div class="info"><strong>Firma:</strong> ${qrPrintModal.order.firm?.firmName || "-"}</div>
              <div class="info"><strong>Model:</strong> ${qrPrintModal.order.model?.modelName || "-"}</div>
              <div class="info"><strong>Miktar:</strong> ${qrPrintModal.order.quantity}</div>
              ${qrPrintModal.order.note ? `<div class="info"><strong>Not:</strong> ${qrPrintModal.order.note}</div>` : ""}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  // İlk yüklemede tam sayfa yükleyici; süzgeç değişince liste kartı soluklaşır (sayfa sökülmez)
  if (loading && !loadedOnce) {
    return <PageLoader message="Siparişler yükleniyor..." />;
  }

  // ---- Görünüm yardımcıları (yalnız sunum) ----
  const isManager = user?.role === "Manager";
  const filtersActive = activeFilterCount > 0 || !!searchTerm.trim();
  const summaryByStatus: Record<string, { Count: number; TotalValue: number; TotalQuantity: number }> = {};
  statusSummary.forEach((s) => { summaryByStatus[s.Status] = s; });
  const kpiDefs: { status: OrderStatus; label: string; cls: string }[] = [
    { status: OrderStatus.Islemde, label: "İşlemde", cls: "navy" },
    { status: OrderStatus.Atanmadi, label: "Atanmadı", cls: "ol-warn" },
    { status: OrderStatus.Tamamlandi, label: "Tamamlandı", cls: "ok" },
  ];
  if ((summaryByStatus["İptal Edildi"]?.Count || 0) > 0) kpiDefs.push({ status: OrderStatus.IptalEdildi, label: "İptal Edildi", cls: "err" });
  const listQty = displayOrders.reduce((t, o) => t + (o.quantity || 0), 0);
  const workshopOptions = (isManager ? workshops : workshops.filter((w) => user?.workshops?.some((uw) => uw.workshopId === w.workshopId)))
    .filter((w) => turkishIncludes(w.name, workshopSearch))
    .sort((a, b) => a.name.localeCompare(b.name));
  const workshopLabel = workshopFilter.length === 0
    ? "Tüm atölyeler"
    : workshopFilter.length === 1
      ? workshops.find((w) => w.workshopId === workshopFilter[0])?.name || "1 atölye"
      : `${workshopFilter.length} atölye`;
  const toggleStatus = (s: OrderStatus) => {
    const next = statusFilter.includes(s) ? statusFilter.filter((x) => x !== s) : [...statusFilter, s];
    setStatusFilter(next);
    if (!(next.length === 1 && next[0] === OrderStatus.Tamamlandi)) setDateFilter({ startDate: "", endDate: "" });
  };
  const closeMenu = () => { setOpenDropdownId(null); setDropdownPosition(null); };
  const openNote = (order: Order) => setNoteModal({ isOpen: true, note: order.note || "", orderCode: order.model?.modelCode || "", modelName: order.model?.modelName || "" });
  const menuOrder = openDropdownId ? displayOrders.find((o) => o.orderId === openDropdownId) : undefined;
  const hasQr = (o: Order) => !!(o.qrCodeUrl || o.qrCode);

  const renderTechnics = (order: Order) => {
    const list = order.orderTechnics || [];
    if (list.length === 0) return <span className="ol-dash">—</span>;
    const open = expandedTechnics.has(order.orderId);
    const shown = list.length <= 3 || open ? list : list.slice(0, 2);
    return (
      <div className="ol-techs">
        {shown.map((ot, i) => <span key={i} className="po-badge muted ol-tech">{ot.technic?.name || "-"}</span>)}
        {list.length > 3 && (
          <button type="button" className="po-link ol-more-link" onClick={() => {
            const next = new Set(expandedTechnics);
            if (open) next.delete(order.orderId); else next.add(order.orderId);
            setExpandedTechnics(next);
          }}>{open ? "Gizle" : `+${list.length - 2} daha`}</button>
        )}
      </div>
    );
  };

  return (
    <div className="po-page ol-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Sipariş</span>
          <h1>Siparişler</h1>
          <p>Siparişlerin durumu, atölye ataması ve terminleri; satır menüsünden detay, düzenleme, QR ve maliyet.</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={exportToExcel} disabled={displayOrders.length === 0} title="Listelenen siparişleri Excel'e aktar">{I.download} Excel'e aktar</button>
          {has("orders:create") && <button type="button" className="po-btn primary" onClick={() => navigate("/orders/new")}>{I.plus} Yeni sipariş</button>}
        </div>
      </header>

      {/* Durum özeti — yalnız Yönetici (tutar içerir); tabloyla aynı kümeden hesaplanır */}
      {isManager && (
        <section className="po-kpis ol-kpis" aria-label="Durum özeti">
          {kpiDefs.map((k) => {
            const s = summaryByStatus[k.label] || { Count: 0, TotalValue: 0, TotalQuantity: 0 };
            const on = statusFilter.length === 1 && statusFilter[0] === k.status;
            return (
              <button key={k.status} type="button" className={`po-kpi ${k.cls} ${on ? "on" : ""}`} aria-pressed={on}
                title={on ? "Durum süzgecini kaldır" : `Yalnız "${k.label}" siparişleri göster`}
                onClick={() => (on ? toggleStatus(k.status) : handleStatusCardClick(k.label))}>
                <span className="l">{k.label}</span>
                <span className="v">{s.Count}<small> sipariş</small></span>
                <span className="s"><b>{formatNumber(s.TotalQuantity)}</b> adet · <b>{formatCurrency(s.TotalValue)}</b></span>
              </button>
            );
          })}
        </section>
      )}

      <section className={`po-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar ol-toolbar">
          <div className="po-search">{I.search}
            <input placeholder="Sipariş no, firma, model, atölye, operatör veya desinatör ara" aria-label="Sipariş ara" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setSearchTerm("")}>{I.close}</button>}
          </div>
          <div className="po-chipgroup" role="group" aria-label="Sipariş durumu">
            {([["active", "Aktif"], ["inactive", "Pasif"], ["all", "Tümü"]] as const).map(([v, l]) => (
              <button key={v} type="button" className={activeStatusFilter === v ? "on" : ""} aria-pressed={activeStatusFilter === v} onClick={() => setActiveStatusFilter(v)}>{l}</button>
            ))}
          </div>
          {/* Yalnız dar ekranda görünür: süzgeçleri aç/kapat */}
          <button type="button" className={`po-btn ol-ftoggle ${mobileFiltersOpen ? "on" : ""}`} aria-expanded={mobileFiltersOpen} onClick={() => setMobileFiltersOpen((v) => !v)}>
            {I.filter} Süzgeçler{activeFilterCount > 0 && <i className="ol-count">{activeFilterCount}</i>}
          </button>

          <div className={`ol-filters ${mobileFiltersOpen ? "open" : ""}`}>
            <div className="po-chipgroup" role="group" aria-label="Durum">
              <button type="button" className={statusFilter.length === 0 ? "on" : ""} aria-pressed={statusFilter.length === 0} onClick={() => setStatusFilter([])}>Tüm durumlar</button>
              {[OrderStatus.Atanmadi, OrderStatus.Islemde, OrderStatus.Tamamlandi, OrderStatus.IptalEdildi].map((s) => (
                <button key={s} type="button" className={statusFilter.includes(s) ? "on" : ""} aria-pressed={statusFilter.includes(s)} onClick={() => toggleStatus(s)}>{getStatusText(s)}</button>
              ))}
            </div>

            <FirmMultiPickerField
              id="orders-firm-filter"
              variant="toolbar"
              value={firmFilter}
              nullLabel="Tüm firmalar"
              allowCreate={false}
              title="Firmaya göre süz"
              onChange={(ids) => setFirmFilter(ids)}
            />

            <div className="ol-ws">
              <button type="button" className={`ol-select ${workshopFilter.length > 0 ? "has" : ""}`} aria-haspopup="listbox" aria-expanded={workshopDropdownOpen}
                title="Atölyeye göre süz" onClick={() => setWorkshopDropdownOpen(!workshopDropdownOpen)}>
                {I.factory}<span>{workshopLabel}</span>{I.caret}
              </button>
              {workshopDropdownOpen && (
                <div className="ol-pop" role="listbox" aria-multiselectable="true" aria-label="Atölyeler">
                  <div className="po-search ol-pop-search">{I.search}
                    <input placeholder="Atölye ara" aria-label="Atölye ara" value={workshopSearch} onChange={(e) => setWorkshopSearch(e.target.value)} autoFocus />
                  </div>
                  <div className="ol-pop-list">
                    <label className="po-check ol-pop-all"><input type="checkbox" checked={workshopFilter.length === 0} onChange={() => setWorkshopFilter([])} />Tüm atölyeler</label>
                    {workshopOptions.map((w) => (
                      <label key={w.workshopId} className="po-check">
                        <input type="checkbox" checked={workshopFilter.includes(w.workshopId)}
                          onChange={() => setWorkshopFilter(workshopFilter.includes(w.workshopId) ? workshopFilter.filter((id) => id !== w.workshopId) : [...workshopFilter, w.workshopId])} />
                        {w.name}
                      </label>
                    ))}
                    {workshopOptions.length === 0 && <span className="po-hint ol-pop-empty">Eşleşen atölye yok</span>}
                  </div>
                </div>
              )}
            </div>

            <button type="button" className={`ol-select ${selectedTechnics.length > 0 ? "has" : ""}`} onClick={() => setShowTechnicModal(true)} title="Tekniğe göre süz">
              {I.layers}<span>{selectedTechnics.length === 0 ? "Teknik seç" : `${selectedTechnics.length} teknik`}</span>{I.caret}
            </button>

            {/* Bitiş tarihi aralığı — yalnız "Tamamlandı" tek başına seçiliyken */}
            {statusFilter.length === 1 && statusFilter.includes(OrderStatus.Tamamlandi) && (
              <div className="ol-dates" role="group" aria-label="Bitiş tarihi aralığı">
                <span>Bitiş</span>
                <input type="date" aria-label="Başlangıç tarihi" value={dateFilter.startDate} onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })} />
                <span>–</span>
                <input type="date" aria-label="Bitiş tarihi" value={dateFilter.endDate} onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })} />
              </div>
            )}

            {activeFilterCount > 0 && <button type="button" className="po-clear" onClick={clearFilters} title="Tüm süzgeçleri temizle">{I.filterOff} Temizle</button>}
          </div>
        </div>

        {/* Teknik süzgeci ayrıntısı — yalnız teknik seçiliyken */}
        {selectedTechnics.length > 0 && (
          <div className="ol-techbar">
            <span className="ol-techbar-l">Eşleşme</span>
            <div className="po-chipgroup" role="radiogroup" aria-label="Teknik eşleşme modu">
              {TECHNIC_MATCH_MODES.map((mode) => (
                <button key={mode.value} type="button" role="radio" aria-checked={technicMatchMode === mode.value}
                  className={technicMatchMode === mode.value ? "on" : ""} onClick={() => setTechnicMatchMode(mode.value)} title={mode.hint}>{mode.label}</button>
              ))}
            </div>
            <span className="po-hint ol-techbar-hint">{getTechnicMatchHint(technicMatchMode)}</span>
            <div className="ol-techchips">
              {selectedTechnics.map((tech) => (
                <span key={tech.technicId} className="ol-techchip">{tech.name}
                  <button type="button" onClick={() => handleTechnicSelect(tech)} aria-label={`${tech.name} süzgeçten kaldır`}>{I.close}</button>
                </span>
              ))}
              <button type="button" className="po-link" onClick={handleClearTechnics}>Tümünü kaldır</button>
            </div>
          </div>
        )}

        <div className="po-card-head">
          <span><b>{displayOrders.length}</b> sipariş{displayOrders.length > 0 && <> · <b>{formatNumber(listQty)}</b> birim</>}</span>
          {orderCf.hasActive && <button type="button" className="po-clear" onClick={orderCf.clearAll}>{I.filterOff} Sütun süzgeçlerini temizle</button>}
        </div>

        {displayOrders.length === 0 ? (
          filtersActive || orderCf.hasActive ? (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Süzgeçle eşleşen sipariş yok</b>Aramayı veya süzgeçleri değiştirin.
              <button type="button" className="po-btn" onClick={() => { clearFilters(); orderCf.clearAll(); }}>Süzgeçleri sıfırla</button></div>
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz sipariş yok</b>İlk siparişi oluşturduğunuzda burada listelenir.
              {has("orders:create") && <button type="button" className="po-btn primary" onClick={() => navigate("/orders/new")}>{I.plus} Yeni sipariş</button>}</div>
          )
        ) : (
          <>
            <div className="po-table-wrap ol-table-wrap">
              <table className="po-table ol-table">
                <thead>
                  <tr>
                    {/* ol-p2/p3/p4: sütun öncelikleri — dar ekranda p4 → p3 → p2 sırasıyla gizlenir */}
                    <FilterTh label="ID" colKey="orderId" ctx={orderCf} className="ol-p4" />
                    <SortFilterTh label="Firma" sortKey="firm" colKey="firm" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                    <SortFilterTh label="Model" sortKey="model" colKey="model" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                    <SortFilterTh label="Miktar" sortKey="quantity" colKey="quantity" type="float" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                    <FilterTh label="Birim" colKey="unit" ctx={orderCf} className="ol-p4" />
                    <FilterTh label="Parça" colKey="pieceCount" type="int" ctx={orderCf} align="left" className="ol-p4" />
                    <th className="ol-p4" title="Numune siparişi">Numune</th>
                    <FilterTh label="Teknikler" colKey="technics" ctx={orderCf} className="ol-p3" />
                    {isManager && (
                      <>
                        <SortFilterTh label="Birim Fiyat" sortKey="price" colKey="price" type="decimal" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} className="ol-p2" />
                        <FilterTh label="Döviz" colKey="currency" ctx={orderCf} className="ol-p3" />
                      </>
                    )}
                    <SortFilterTh label="Atölye" sortKey="workshop" colKey="workshop" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} className="ol-p2" />
                    <SortFilterTh label="Desinatör" sortKey="modelist" colKey="modelist" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} className="ol-p3" />
                    <SortFilterTh label="Durum" sortKey="status" colKey="status" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                    <SortFilterTh label="Kabul" sortKey="acceptanceDate" colKey="acceptanceDate" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} className="ol-p2" />
                    <SortFilterTh label="Termin" sortKey="deadline" colKey="deadline" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                    <SortFilterTh label="Bitiş" sortKey="completionDate" colKey="completionDate" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} className="ol-p3" />
                    <FilterTh label="Not" colKey="note" ctx={orderCf} className="ol-p4" />
                    <th className="act"><span className="ol-sr">İşlemler</span></th>
                  </tr>
                </thead>
                <tbody>
                  {displayOrders.map((order) => {
                    const late = isLate(order);
                    const unit = getUnitText(order.unit);
                    return (
                      <tr key={order.orderId} className={`static ${openDropdownId === order.orderId ? "on" : ""} ${order.status === OrderStatus.IptalEdildi ? "off" : ""}`}>
                        <td className="ol-p4 ol-id"><CopyIdButton value={order.orderId} /></td>
                        <td><span className="po-name ol-firm" title={order.firm?.firmName || ""}>{order.firm?.firmName || "—"}</span></td>
                        <td>
                          <div className="ol-model" title={order.model?.modelName || ""}>{order.model?.modelName || "—"}</div>
                          {order.model?.modelCode && order.model.modelCode !== order.model.modelName && <div className="po-sub">{order.model.modelCode}</div>}
                        </td>
                        <td className="ol-qty"><span className="po-amt">{formatNumber(order.quantity || 0)}</span> <span className="ol-unit">{unit.toLocaleLowerCase("tr-TR")}</span></td>
                        <td className="ol-p4">{unit}</td>
                        <td className="ol-p4">{order.unit === OrderUnit.Takim && order.pieceCount ? `${order.pieceCount} parça` : <span className="ol-dash">—</span>}</td>
                        <td className="ol-p4">
                          <input type="checkbox" className="ol-cb" checked={order.isSample || false} onChange={() => handleToggleSample(order)} disabled={loading}
                            title={order.isSample ? "Numune siparişi" : "Normal sipariş"} aria-label="Numune siparişi" />
                        </td>
                        <td className="ol-p3">{renderTechnics(order)}</td>
                        {isManager && (
                          <>
                            <td className="ol-p2 num">{order.price ? formatNumber(order.price) : <span className="ol-dash">—</span>}</td>
                            <td className="ol-p3"><span className="po-codepill ol-cur">{order.priceCurrency || order.currency || "TRY"}</span></td>
                          </>
                        )}
                        <td className="ol-p2">{order.workshop?.name || <span className="ol-dash">—</span>}</td>
                        <td className="ol-p3">{order.modelistUser ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}` : <span className="ol-dash">—</span>}</td>
                        <td><span className={`po-badge ${statusBadge(order.status)}`}>{getStatusText(order.status)}</span></td>
                        <td className="ol-p2 po-due">{order.acceptanceDate ? formatDate(order.acceptanceDate) : <span className="ol-dash">—</span>}</td>
                        <td className={`po-due ${late ? "late" : ""}`}>{order.deadline ? formatDate(order.deadline) : <span className="ol-dash">—</span>}{late && <div className="po-sub">gecikti</div>}</td>
                        <td className="ol-p3 po-due">
                          {order.completionDate ? formatDate(order.completionDate) : order.status === OrderStatus.Tamamlandi ? <span className="po-sub">Tarih yok</span> : <span className="ol-dash">—</span>}
                        </td>
                        <td className="ol-p4">
                          {order.note && order.note.trim() ? (
                            <button type="button" className="ol-notebtn" onClick={() => openNote(order)} title="Notu görüntüle">
                              {I.note}<span>{order.note}</span>
                            </button>
                          ) : <span className="ol-dash">—</span>}
                        </td>
                        <td className="act">
                          <div className="ol-actwrap">
                            {isManager && (
                              <button type="button" className="po-ib" title="Detay" aria-label="Sipariş detayı" onClick={() => setDetailModal({ isOpen: true, orderId: order.orderId })}>{I.eye}</button>
                            )}
                            <button type="button" className={`po-ib ol-menubtn ${openDropdownId === order.orderId ? "on" : ""}`} title="İşlemler" aria-label="Sipariş işlemleri"
                              aria-haspopup="menu" aria-expanded={openDropdownId === order.orderId}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openDropdownId === order.orderId) return closeMenu();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
                                const menuHeight = 19 * rootPx; // ~8 öğe
                                const shouldOpenUp = window.innerHeight - rect.bottom < menuHeight && rect.top > menuHeight;
                                setDropdownPosition({
                                  top: shouldOpenUp ? rect.top - menuHeight : rect.bottom + 4,
                                  left: Math.max(8, rect.right - 14 * rootPx), // menü genişliği 14rem
                                });
                                setOpenDropdownId(order.orderId);
                              }}>{I.dots}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Dar ekran: kart listesi (tablo ile aynı veri ve yetki kapıları) */}
            <ul className="ol-cards">
              {displayOrders.map((order) => {
                const late = isLate(order);
                return (
                  <li key={order.orderId} className={`ol-card ${order.status === OrderStatus.IptalEdildi ? "off" : ""}`}>
                    <div className="ol-card-top">
                      <div className="ol-card-title">
                        <b title={order.firm?.firmName || ""}>{order.firm?.firmName || "Firma bilinmiyor"}</b>
                        <span>{order.model?.modelName || "Model bilinmiyor"}{order.model?.modelCode && order.model.modelCode !== order.model.modelName ? ` · ${order.model.modelCode}` : ""}</span>
                      </div>
                      <span className={`po-badge ${statusBadge(order.status)}`}>{getStatusText(order.status)}</span>
                    </div>
                    <dl className="ol-card-facts">
                      <div><dt>Miktar</dt><dd>{formatNumber(order.quantity || 0)} {getUnitText(order.unit)}{order.unit === OrderUnit.Takim && order.pieceCount ? ` (${order.pieceCount} parça)` : ""}</dd></div>
                      <div className={late ? "late" : ""}><dt>Termin</dt><dd>{formatDate(order.deadline)}{late && " · gecikti"}</dd></div>
                      <div><dt>Kabul</dt><dd>{formatDate(order.acceptanceDate)}</dd></div>
                      <div><dt>Atölye</dt><dd>{order.workshop?.name || "—"}</dd></div>
                      {order.modelistUser && <div><dt>Desinatör</dt><dd>{order.modelistUser.firstName} {order.modelistUser.lastName}</dd></div>}
                      <div><dt>Numune</dt><dd><label className="po-check"><input type="checkbox" checked={order.isSample || false} onChange={() => handleToggleSample(order)} disabled={loading} />{order.isSample ? "Evet" : "Hayır"}</label></dd></div>
                    </dl>
                    {order.note && order.note.trim() && (
                      <button type="button" className="ol-notebtn wide" onClick={() => openNote(order)}>{I.note}<span>{order.note}</span></button>
                    )}
                    <div className="ol-card-acts">
                      <button type="button" className="po-btn small primary" onClick={() => setDetailModal({ isOpen: true, orderId: order.orderId })}>{I.eye} Detay</button>
                      {canUpdateOrder && <button type="button" className="po-btn small" onClick={() => setEditModal({ isOpen: true, orderId: order.orderId })}>{I.edit} Düzenle</button>}
                      {user && <button type="button" className="po-btn small" onClick={() => navigate(`/orderdetail/${order.orderId}`)}>{I.transfer} Transfer</button>}
                      <button type="button" className="po-btn small" onClick={() => { setSelectedOrderForImages(order.orderId); setShowImageManager(true); }}>{I.image} Resimler</button>
                      {hasQr(order) && <button type="button" className="po-btn small" onClick={() => handleShowQrModal(order)}>{I.qr} QR</button>}
                      {canViewModelCosts && order.modelId && (
                        <button type="button" className="po-btn small" onClick={() => setOrderCostsModal({ isOpen: true, orderId: order.orderId, modelId: order.modelId! })}>{I.money} Maliyet</button>
                      )}
                      {canDeleteOrder && <button type="button" className="po-btn small danger" onClick={() => handleDeleteOrder(order)}>{I.trash} Sil</button>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* Satır menüsü — sabit konumlu; kaydırınca / dışarı tıklayınca / Esc ile kapanır */}
      {menuOrder && dropdownPosition && (
        <div className="ol-menu" role="menu" aria-label="Sipariş işlemleri" style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
          {isManager && <button type="button" role="menuitem" onClick={() => { closeMenu(); setDetailModal({ isOpen: true, orderId: menuOrder.orderId }); }}>{I.eye} Detay</button>}
          {canUpdateOrder && <button type="button" role="menuitem" onClick={() => { closeMenu(); setEditModal({ isOpen: true, orderId: menuOrder.orderId }); }}>{I.edit} Düzenle</button>}
          {user && <a role="menuitem" href={`/orderdetail/${menuOrder.orderId}`} onClick={(e) => e.stopPropagation()}>{I.transfer} Transfer</a>}
          <button type="button" role="menuitem" onClick={() => { closeMenu(); setSelectedOrderForImages(menuOrder.orderId); setShowImageManager(true); }}>{I.image} Resimleri görüntüle</button>
          {hasQr(menuOrder) && <button type="button" role="menuitem" onClick={() => { closeMenu(); handleShowQrModal(menuOrder); }}>{I.qr} QR detayı</button>}
          {canViewModelCosts && menuOrder.modelId && (
            <button type="button" role="menuitem" onClick={() => { closeMenu(); setOrderCostsModal({ isOpen: true, orderId: menuOrder.orderId, modelId: menuOrder.modelId! }); }}>{I.money} Sipariş maliyetleri</button>
          )}
          {canDeleteOrder && <>
            <hr />
            <button type="button" role="menuitem" className="danger" onClick={() => { closeMenu(); handleDeleteOrder(menuOrder); }}>{I.trash} Sil</button>
          </>}
        </div>
      )}

      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleTechnicSelect}
        selectedTechnics={selectedTechnics}
        onClearAll={handleClearTechnics}
        workshops={workshops}
      />

      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}

      {/* QR detayı */}
      {qrPrintModal.show && qrPrintModal.order && (
        <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) closeQr(); }}>
          <div className="po-modal ol-qr" role="dialog" aria-modal="true" aria-labelledby="ol-qr-title">
            <div className="po-modal-head">
              <div><span className="po-kicker">Sipariş QR kodu</span><h3 id="ol-qr-title">{qrPrintModal.order.model?.modelName || "Sipariş"}</h3></div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeQr} autoFocus>{I.close}</button>
            </div>
            <div className="po-modal-body">
              <dl className="po-facts">
                <div><dt>Firma</dt><dd>{qrPrintModal.order.firm?.firmName || "-"}</dd></div>
                <div><dt>Miktar</dt><dd>{formatNumber(qrPrintModal.order.quantity || 0)} {getUnitText(qrPrintModal.order.unit)}</dd></div>
                <div><dt>Atölye</dt><dd>{qrPrintModal.order.workshop?.name || "-"}</dd></div>
                <div><dt>Sipariş no</dt><dd className="ol-qr-id">{qrPrintModal.order.orderId}</dd></div>
                {qrPrintModal.order.note && <div className="wide"><dt>Not</dt><dd>{qrPrintModal.order.note}</dd></div>}
              </dl>
              {qrPrintModal.qrCodeDataUrl ? (
                <div className="ol-qr-box"><img src={qrPrintModal.qrCodeDataUrl} alt="Sipariş QR kodu" /></div>
              ) : (
                <div className="po-note warn">QR kod oluşturulamadı.</div>
              )}
            </div>
            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={closeQr}>Kapat</button>
              {qrPrintModal.qrCodeDataUrl && <>
                <a className="po-btn" href={qrPrintModal.qrCodeDataUrl} download={`order-${qrPrintModal.order.orderId}-qr.png`}>{I.download} İndir</a>
                <button type="button" className="po-btn primary" onClick={printQr}>{I.print} Yazdır</button>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={detailModal.isOpen}
        orderId={detailModal.orderId}
        onClose={() => setDetailModal({ isOpen: false, orderId: "" })}
      />

      {/* Order Edit Modal */}
      <OrderEditModal
        isOpen={editModal.isOpen}
        orderId={editModal.orderId}
        onClose={() => setEditModal({ isOpen: false, orderId: "" })}
        onSave={() => {
          setEditModal({ isOpen: false, orderId: "" });
          loadOrders();
        }}
      />

      {/* Order Costs Modal */}
      <OrderCostsModal
        isOpen={orderCostsModal.isOpen}
        orderId={orderCostsModal.orderId}
        modelId={orderCostsModal.modelId}
        onClose={() =>
          setOrderCostsModal({ isOpen: false, orderId: "", modelId: "" })
        }
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type="danger"
        confirmText="Sil"
        cancelText="İptal"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Note Modal */}
      <NoteModal
        isOpen={noteModal.isOpen}
        note={noteModal.note}
        orderCode={noteModal.orderCode}
        modelName={noteModal.modelName}
        onClose={() =>
          setNoteModal({
            isOpen: false,
            note: "",
            orderCode: "",
            modelName: "",
          })
        }
      />
    </div>
  );
};

export default Orders;
